import React, { ReactElement, useCallback, useMemo, useState } from 'react';

import { BusinessUnitWebSdkTable } from 'src/apps/dashboard/components/business-unit-web-sdk-table/BusinessUnitWebSdkTable';
import { BusinessUnitWfaTable } from 'src/apps/dashboard/components/business-unit-wfa-table/BusinessUnitWfaTable';
import { useShowOnlyLastReportToggle } from 'src/apps/dashboard/hooks/useShowOnlyLastReportToggle';
import { useDashboardFiltersStore } from 'src/common/stores/dashboardFiltersStore';

import { createBusinessUnitSubTabOptions } from '../helpers/TabNavigationHelpers';
import { BusinessUnitSubTabId } from '../types/DashboardTypes';

interface UseBusinessUnitTabsParams {
	businessUnitName: string;
}

interface UseBusinessUnitTabsReturn {
	activeTabIndex: number;
	setActiveTabIndex: (index: number) => void;
	tabs: Array<{
		id: string;
		tablistId: string;
		ref: null;
		isActive: boolean;
		handleKeyDown: () => void;
		children: string;
		ariaControls: string;
		onActivate: () => void;
		dataAutomationId: string;
	}>;
	tabPanels: Array<{
		id: string;
		ariaLabelledBy: string;
		isActive: boolean;
		children: ReactElement;
	}>;
}

export const useBusinessUnitTabs = ({
	businessUnitName
}: UseBusinessUnitTabsParams): UseBusinessUnitTabsReturn => {
	const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
	const { filters } = useDashboardFiltersStore();
	const { showOnlyLastReport, showOnlyLastReportToggle } = useShowOnlyLastReportToggle();

	// Render tab content based on sub-tab ID
	const renderTabContent = useCallback(
		(subTabId: BusinessUnitSubTabId): ReactElement => {
			switch (subTabId) {
				case BusinessUnitSubTabId.WEB_SDKS:
					return (
						<div className="web-sdks-content">
							<BusinessUnitWebSdkTable
								dashboardFilters={filters}
								unitIdLabel={businessUnitName}
								showOnlyLastReport={showOnlyLastReport}
								topRowActions={showOnlyLastReportToggle}
							/>
						</div>
					);
				case BusinessUnitSubTabId.WEB_FLOW_ANALYZER:
					return (
						<div className="web-flow-analyzer-content">
							<BusinessUnitWfaTable dashboardFilters={filters} unitIdLabel={businessUnitName} />
						</div>
					);
				default:
					return (
						<div className="default-tab-content">
							<p>Content for this tab is not yet implemented.</p>
						</div>
					);
			}
		},
		[businessUnitName, filters, showOnlyLastReport, showOnlyLastReportToggle]
	);

	// Create tabs and tab panels for EvTabs
	const { tabs, tabPanels } = useMemo(() => {
		const subTabOptions = createBusinessUnitSubTabOptions();

		const tabs = subTabOptions.map((option, index) => ({
			id: `tab-${option.id}`,
			tablistId: 'business-unit-tabs',
			ref: null, // EvTabs handles refs internally
			isActive: index === activeTabIndex,
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			handleKeyDown: () => {}, // EvTabs handles keyboard navigation internally
			children: option.title,
			ariaControls: `panel-${option.id}`,
			onActivate: () => setActiveTabIndex(index),
			dataAutomationId: `tab-${option.id}`
		}));

		const tabPanels = subTabOptions.map((option, index) => ({
			id: `panel-${option.id}`,
			ariaLabelledBy: `tab-${option.id}`,
			isActive: index === activeTabIndex,
			children: (
				<div className="sub-tab-content">{renderTabContent(option.id as BusinessUnitSubTabId)}</div>
			)
		}));

		return { tabs, tabPanels };
	}, [activeTabIndex, renderTabContent]);

	return {
		activeTabIndex,
		setActiveTabIndex,
		tabs,
		tabPanels
	};
};
